import React, { useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { ArrowDownCircleIcon, TicketIcon } from "@heroicons/react/24/solid";
import { CSVLink } from "react-csv";
import MaterialTableTS from "../../../../components/tables/MaterialTableTS";
import useGetScorecardReportDaily from "./hooks/useGetScorecardReportDaily";
import { mainSelectedTab } from "../../atoms";
import { useHistory } from "react-router-dom";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import DownloadButton from "../../../../components/DownloadButton";

interface ScorecardReportOpenProps {}
export const ScorecardReportOpen: React.FC<ScorecardReportOpenProps> = () => {
  let today = new Date().toLocaleString();
  const searchInputsData = {
    Account: "",
    CaseNumber: "",
    Contact: "",
    CreatedBy: "",
    CreatedDate: "",
    ReportName: "ScorecardReportOpen",
    Status: "",
    ExcludeStatus:
      "'80-Closed','81-Notified Agent and Closed','82-Closed - No Response'",
    ActionRequiredOrNotificationC: "",
    ContactId: null,
  };

  const {
    data: searchScorecard,
    isLoading: searchScorecardLoading,
    isSuccess,
    refetch,
  } = useGetScorecardReportDaily(searchInputsData);

  useEffect(() => {
    if (!searchScorecard) {
      refetch();
    }
  }, [searchScorecard]);

  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const history = useHistory();
  const userPermissions = useRecoilValue(permissionsSelector);

  const GetDaysBetweenDates = (d0, d1) => {
    let msPerDay = 8.64e7; // milliseconds in a day
    let x0 = new Date(d0);
    let x1 = new Date(d1);
    return Math.round(
      (x0.setHours(12, 0, 0) - x1.setHours(12, 0, 0)) / msPerDay
    );
  };

  const filteredScorecard = searchScorecard?.filter((x) =>
    x.closedDate
      ? GetDaysBetweenDates(x.closedDate, x.createdDate)
      : GetDaysBetweenDates(today, x.createdDate) >= 14
  );

  interface IScorecardReportDaily {
    closeReasonC: string;
    createdBy: string;
    caseNumber: string;
    contact: string;
    account: string;
    type: string;
    subject: string;
    policyC: string;
    customerC: string;
    amountC: string;
    actionRequiredOrNotificationC: string;
    generalGroupC: string;
    descriptiveGroupC: string;
    scorecardAreaC: string;
    actionToTakeC: string;
    scorecardAreaOtherC: string;
    writeOffAmountC: string;
    createdDate: string;
    closedDate: string;
    status: string;
    reportsToFullName: string;
    state: string;
    districtManager: string;
  }

  //For downloading table data
  const exportdata = filteredScorecard?.map((scrd: IScorecardReportDaily) => {
    return {
      "Created By": scrd.createdBy,
      "Case Number": scrd.caseNumber,
      Open: "True",
      Status: scrd.status,
      "Date/Time Opened": scrd.createdDate
        ? new Date(scrd.createdDate)?.toLocaleString()
        : "",
      "Days Case Open": scrd.closedDate
        ? GetDaysBetweenDates(
            scrd.closedDate === "1900-01-01T00:00:00" ? (
              <div>{"N/A"}</div>
            ) : (
              scrd.closedDate
            ),
            scrd.createdDate
          )
        : GetDaysBetweenDates(today, scrd.createdDate),
      "Employee Name": scrd.contact,
      "Reports To 1": scrd.reportsToFullName,
      "Office Name": scrd.account,
      State: scrd.state,
      "District Manager": scrd.districtManager,
      Subject: scrd.subject,
    };
  });

  const csvReport = {
    filename: "ScorecardReportDaily_" + today + ".csv",
    data: exportdata ? exportdata : [],
  };

  const officeColumns = [
    { title: "Created By", field: "createdBy" },
    {
      title: "Case Number",
      field: "caseNumber",
    },
    { title: "Open", render: (rowData) => "True" },
    { title: "Status", field: "status" },
    {
      title: "Date/Time Opened",
      field: "createdDate",
      render: (rowData) =>
        rowData.createdDate
          ? new Date(rowData.createdDate)?.toLocaleString()
          : "",
    },
    {
      title: "Days Case Open",
      field: "",
      render: (rowData) =>
        rowData.closedDate
          ? GetDaysBetweenDates(
              rowData.closedDate === "1900-01-01T00:00:00" ? (
                <div>{"N/A"}</div>
              ) : (
                rowData.closedDate
              ),
              rowData.createdDate
            )
          : GetDaysBetweenDates(today, rowData.createdDate),
    },
    { title: "Employee Name", field: "contact" },
    { title: "Reports To 1", field: "reportsToFullName" },
    {
      title: "Office Name",
      field: "account",
      render: (rowData) => {
        return (
          <div
            className="cursor-pointer"
            onClick={() => {
              const url = "/officesettings/officesetting";
              history.push({
                pathname: url,
                state: {
                  officeId: rowData?.account,
                  action: "ScorecardReportOpen",
                },
              });
              window.location.reload();
              let userTabs = [
                {
                  to: "",
                },
              ];
              [...OfficeTabs].forEach((tabs) => {
                let temp = [...userPermissions].filter(
                  (user) => user === tabs.permission
                );
                if (temp.length > 0)
                  userTabs.push({
                    to: tabs.to,
                  });
              });
              userTabs.shift();
              const index = userTabs.findIndex((c) => c.to === url);
              setSelectedIndex(index);
            }}
          >
            <span className="text-quinary  font-bold underline text-sm ">
              {rowData.account}
            </span>
          </div>
        );
      },
    },
    { title: "State", field: "state" },
    { title: "District Manager", field: "districtManager" },
    { title: "Subject", field: "subject" },
  ];

  return (
    <div className="text-xs items-center">
      <div className="">
        <div className="mx-4 mt-4 overflow-x-auto">
          <div className="flex mb-2 gap-2 items-center">
            <div>
              <TicketIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
            </div>
            <div className="flex flex-col leading-tight">
              <div className="text-gray-500 text-md font-medium font-medium">
                Report: Scorecard
              </div>
              <div className="flex items-center font-bold text-lg text-primary">
                {"Open Scorecards > 14 days"}
              </div>
            </div>
          </div>
          <div className="mt-2" />
          <div className="flex justify-between">
            <div className="flex items-center gap-1">
              <span className="text-gray-500 text-sm">Total Records: </span>
              <span className="text-primary text-base font-semibold">
                {filteredScorecard?.length}
              </span>
            </div>

            <CSVLink {...csvReport}>
              <DownloadButton
                buttonValue="Download File"
                onClick={() => exportdata()}
                className={undefined}
                disabled={undefined}
              />
            </CSVLink>
          </div>
        </div>
        <div className="box-shadow mx-4 mb-4 mt-2 overflow-x-auto grid grid-row">
          <MaterialTableTS
            data={filteredScorecard}
            isLoading={searchScorecardLoading}
            columns={officeColumns}
            showTitle={false}
          />
        </div>
      </div>
    </div>
  );
};
